import { BaseWebComponentElement } from './BaseWebComponentElement';
import _ from 'lodash';
import { CommonEvents } from '@nirvana/shared/constants';

/**
 * This component should be used when a webcomponent form must be implemented and included
 * as a form field inside an existing ExtJs form, while being able to handle complex forms.
 * It is also intended to give support for value storing and validations.
 */
export class BaseFormFieldWebComponentElement<FormFieldType> extends BaseWebComponentElement {
  private wasValid?: boolean;

  /**
   * Also used in ExtJs (must return string)
   */
  get value(): string {
    const value = this.getAttribute('value');
    return value ?? '';
  }

  /**
   * Also used in ExtJs (must use string)
   */
  set value(value: string) {
    this.setAttribute('value', value);
  }

  /**
   * Get form value as FormFieldType type
   */
  public getFormValue(): FormFieldType | null {
    return this.value ? JSON.parse(this.value) : null;
  }

  /**
   * Set form value using a FormFieldType value
   * Also used in ExtJs
   */
  public setFormValue(value: FormFieldType | null) {
    const newValue = JSON.stringify(value ?? '');
    this.setAttribute('value', newValue);
  }

  /**
   * Validates the form. Should be implemented if form validations are required.
   * Also used in ExtJs at validity checking of the bigger form during recursive validations.
   */
  public isValid(): boolean {
    return true;
  }

  /**
   * Clears the form value and reset other form properties
   * Also used in ExtJs
   */
  public reset() {
    this.value = '';
    delete this.wasValid;
  }

  /**
   * Updates the "value" attribute of webcomponent with the latest changes, if any changes
   * in a form field are detected. Also, emits a validity change check in the end.
   *
   * Warning: Must be called only when FormFieldType is an interface or an object. Otherwise,
   * a custom change handler must be implemented.
   *
   * @param formFieldKey The key of the form field to be updated. Supports dot notation path.
   * @param newFormFieldValue The new detected value after form field change
   */
  public onFormFieldChange(formFieldKey: string, newFormFieldValue: any): void {
    const formValue = this.getFormValue();
    const keyPath = formFieldKey.split('.');
    const oldFormFieldValue = _.get(formValue, keyPath, null);

    if (!formValue || _.isEqual(oldFormFieldValue, newFormFieldValue)) {
      return;
    }

    const formValueObject = formValue as unknown as object;

    _.set(formValueObject, keyPath, newFormFieldValue);
    this.setFormValue(formValueObject as unknown as FormFieldType);
    this.checkValidityChange();
  }

  /**
   * Checks if validity of the form has changed from the last time it has been checked.
   */
  public checkValidityChange() {
    const isValid = this.isValid();

    if (isValid === this.wasValid) {
      // No change in validity to report
      return;
    }

    // Update wasValid and dispatch event that will be handled in ExtJs, triggering the call for isValid() method
    this.wasValid = isValid;
    this.dispatchEvent(
      new CustomEvent(CommonEvents.VALIDITY_CHANGE, {
        bubbles: true,
        composed: true,
      })
    );
  }
}
